import React, { useCallback, useState, useEffect, useMemo } from 'react';
import Button from '@material-ui/core/Button';
import Fade from '@material-ui/core/Fade';
import SendIcon from '@mui/icons-material/Send';
import { makeStyles, Theme } from '@material-ui/core';
import { createStyles } from '@material-ui/styles';

import { AutosizeInput } from '../AutosizeInput';
import { useImpactAiContext } from '../hooks/ImpactAiContextProvider';

import { useSharedWidgetStyles } from './sharedWidgetStyles';

const useTemplateInputStyles = makeStyles((theme: Theme) =>
  createStyles({
    templateContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      padding: theme.spacing(1, 2),
      paddingRight: theme.spacing(7),
      backgroundColor: '#F4F4F6',
      border: '2px solid transparent',
      position: 'relative',
      lineHeight: 1.6,
      fontSize: theme.typography.body1.fontSize,
      // User interaction widgets: sharper edge on the right
      borderRadius: `${theme.spacing(2)}px ${theme.spacing(2)}px 0 ${theme.spacing(2)}px`,
      maxWidth: '80%',
      width: '100%',
    },
    templateInput: {
      display: 'inline-flex',
      margin: `0 ${theme.spacing(0.25)}px`,
      width: 'auto',
      minWidth: '40px',
      backgroundColor: '#DCD4EE',
      borderRadius: theme.shape.borderRadius / 2,
      padding: theme.spacing(0.25, 0.75),
      paddingLeft: theme.spacing(1),
      fontSize: theme.typography.body1.fontSize,
      lineHeight: 1.2,
      height: '28px',
      verticalAlign: 'middle',
      transition: theme.transitions.create('width', {
        duration: theme.transitions.duration.shortest,
      }),
      position: 'relative',
      '& input': {
        padding: 0,
        fontSize: theme.typography.body1.fontSize,
        paddingRight: theme.spacing(0.5),
        paddingLeft: theme.spacing(0.5),
      },
      marginBottom: '4px',
    },
  })
);

interface TemplateVariable {
  name: string;
  placeholder: string;
}

interface TemplateInputProps {
  template: string;
  onSubmit: (completedTemplate: string) => void;
  isProcessingAction: boolean;
}

const extractTemplateVariables = (template: string): TemplateVariable[] => {
  const matches = template.match(/\{\{([^}]+)\}\}/g) || [];
  return matches.map((match) => {
    const content = match.replace(/\{\{|\}\}/g, '');
    const parts = content.split(',');

    return {
      name: parts[0].trim(),
      placeholder: parts.length > 1 ? parts.slice(1).join(',').trim() : '',
    };
  });
};

const TemplateInput: React.FC<TemplateInputProps> = ({
  template,
  onSubmit,
  isProcessingAction,
}) => {
  const classes = useTemplateInputStyles();
  const sharedClasses = useSharedWidgetStyles();
  const contextApi = useImpactAiContext();

  const [localValues, setLocalValues] = useState<Record<string, string>>({});

  const parts = template.split(/(\{\{[^}]+\}\})/g);
  const allVariables = useMemo(() => extractTemplateVariables(template), [template]);

  useEffect(() => {
    const initialValues: Record<string, string> = {};
    allVariables.forEach((templateVariable) => {
      const contextValue = contextApi.getValue(templateVariable.name, '');
      initialValues[templateVariable.name] = typeof contextValue === 'string' ? contextValue : '';
    });
    setLocalValues(initialValues);
  }, [template, allVariables]); // eslint-disable-line react-hooks/exhaustive-deps

  const allFieldsFilled = allVariables.every((templateVariable) => {
    const value = localValues[templateVariable.name] || '';
    return value.trim();
  });

  const handleUserInput = useCallback((variable: string, value: string) => {
    setLocalValues((prev) => ({
      ...prev,
      [variable]: value,
    }));
  }, []);

  const handleSubmit = useCallback(() => {
    const completedTemplate = template.replace(/\{\{([^}]+)\}\}/g, (_, content: string) => {
      const [variableName] = content.split(',');
      const variable = variableName.trim();
      const value = localValues[variable] || '';
      return value !== undefined ? String(value) : `{{${variable}}}`;
    });

    // Batch all context updates into a single call
    const contextUpdates: Record<string, string> = {};
    allVariables.forEach((templateVariable) => {
      const value = localValues[templateVariable.name] || '';
      if (value) {
        contextUpdates[templateVariable.name] = value;
      }
    });

    // Update context with all values at once if there are any updates
    if (Object.keys(contextUpdates).length > 0) {
      contextApi.updateValues(contextUpdates);
    }

    onSubmit(completedTemplate);
  }, [template, localValues, onSubmit, allVariables, contextApi]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey && allFieldsFilled) {
        event.preventDefault();
        handleSubmit();
      }
    },
    [allFieldsFilled, handleSubmit]
  );

  return (
    <Fade in={!isProcessingAction} timeout={300} mountOnEnter unmountOnExit>
      <div className={sharedClasses.rightJustifiedWidgetContainer}>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div className={classes.templateContainer} onKeyDown={handleKeyDown} role="presentation">
          {parts.map((part, index) => {
            const match = part.match(/\{\{([^}]+)\}\}/);

            if (match) {
              const content = match[1];
              const [variableName, ...placeholderParts] = content.split(',');
              const variable = variableName.trim();
              const placeholder = placeholderParts.join(',').trim();

              return (
                <AutosizeInput
                  key={`var-${variable}`}
                  value={String(localValues[variable] || '')}
                  onChange={(e) => handleUserInput(variable, e.target.value)}
                  placeholder={placeholder}
                  className={classes.templateInput}
                />
              );
            }

            // TODO@theronburger Do this better is breaking things
            // eslint-disable-next-line react/no-array-index-key
            return <span key={`text-${part.substring(0, 10)}-${index}`}>{part}</span>;
          })}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            className={sharedClasses.sendButton}
            disabled={!allFieldsFilled}
          >
            <SendIcon className={sharedClasses.sendIcon} />
          </Button>
        </div>
      </div>
    </Fade>
  );
};

export default TemplateInput;
